import React, { useEffect } from 'react';

//Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

//Components
import { Switch, Route, Link, Redirect } from 'react-router-dom';
import { Container, Carousel } from 'react-bootstrap';
import { Nav } from 'react-bootstrap';
import ScreenHeader from '../../../../components/ScreenHeader/ScreenHeader';

//Routes
import Services from '../Services/Services';
import Bundles from '../Bundles/Bundles';
import Events from '../Events/Events';
import Groups from '../Groups/Groups';
import Subscriptions from '../Subscriptions'
import Loader from 'react-loader-spinner';

import { WIDGET_STYLE } from '../../../../const/app/WIDGET_STYLE';
import { ROUTER_PATH } from '../../../../const/ROUTER_PATH';
import { ROUTER_URL } from '../../../../const/ROUTER_URL';

import * as COMPANIES_SELECTORS from '../../../../store/selectors/companies';

import * as COMPANIES_ACTIONS from '../../../../store/actions/companies';

import Styles from './Shop.module.scss';

const Shop = () => {
    const { shopId } = useParams();

    const dispatch = useDispatch();
    const { shopLoading, shop } = useSelector(COMPANIES_SELECTORS.companiesSelector);
    const homeGallery = useSelector(state => state.companies.selectedCompany.company.home_gallery)
    const {
        services: showServices,
        groups: showGroups,
        events: showEvents,
        bundles: showBundles,
        subscription: showSubscriptions,
    } = useSelector(state => state.companies.shop ? state.companies.shop.products : {});
    const {
        overwriteProductNames,
        servicesTabName,
        groupsTabName,
        eventsTabName,
        bundlesTabName,
        subscriptionsTabName,
    } = useSelector(state => ({
        overwriteProductNames: state.companies.selectedCompany.company.overwrite_product_names,
        servicesTabName: state.companies.selectedCompany.company.services_tab_name,
        groupsTabName: state.companies.selectedCompany.company.groups_tab_name,
        eventsTabName: state.companies.selectedCompany.company.events_tab_name,
        bundlesTabName: state.companies.selectedCompany.company.bundles_tab_name,
        subscriptionsTabName: state.companies.selectedCompany.company.subscription_tab_name,
    }));

    const style = useSelector(state => state.widget.style);

    const websiteOrDevStyle = [WIDGET_STYLE.DEV, WIDGET_STYLE.WEBSITE].includes(style);

    const { t } = useTranslation('common');

    const servicesMatch = useRouteMatch({
        path: ROUTER_PATH.SHOPS_ID_SERVICES,
    });
    const eventsMatch = useRouteMatch({
        path: ROUTER_PATH.SHOPS_ID_EVENTS,
    });
    const bundlesMatch = useRouteMatch({
        path: ROUTER_PATH.SHOPS_ID_BUNDLES,
    });
    const groupsMatch = useRouteMatch({
        path: ROUTER_PATH.SHOPS_ID_GROUPS,
    });
    const subscriptionsMatch = useRouteMatch({
        path: ROUTER_PATH.SHOPS_ID_SUBSCRIPTIONS,
    });

    const servicesTabLabel = (
        (
            overwriteProductNames &&
            servicesTabName &&
            servicesTabName.length !== 0
        )
            ? servicesTabName
            : t('homeScreen.services')
    );

    const groupsTabLabel = (
        (
            overwriteProductNames &&
            groupsTabName &&
            groupsTabName.length !== 0
        )
            ? groupsTabName
            : t('homeScreen.groupClasses.groupClasses')
    );

    const eventsTabLabel = (
        (
            overwriteProductNames &&
            eventsTabName &&
            eventsTabName.length !== 0
        )
            ? eventsTabName
            : t('homeScreen.events.events')
    );

    const subscriptionsTabLabel = (
        (
            overwriteProductNames &&
            subscriptionsTabName &&
            subscriptionsTabName.length !== 0
        )
            ? subscriptionsTabName
            : t('homeScreen.subscriptions.subscriptions')
    );

    const bundlesTabLabel = (
        (
            overwriteProductNames &&
            bundlesTabName &&
            bundlesTabName.length !== 0
        )
            ? bundlesTabName
            : t('homeScreen.bundles.bundles')
    );

    useEffect(() => {
        if(!shopId || isNaN(shopId)) {
            return;
        }
        dispatch(COMPANIES_ACTIONS.getShop(shopId));
    }, [shopId, dispatch]);

    if (shopLoading || !shop) {
        return (
            <div className={Styles.centeredContainer}>
                <Loader
                    type="TailSpin"
                    color="#000"
                    height={48}
                    width={48}
                />
            </div>
        );
    }

    return (
        <>
            {websiteOrDevStyle && (homeGallery.length > 0) && (
                <Container>
                    <Carousel controls={false}>
                        {
                            homeGallery.map((val, index) => (
                                <Carousel.Item key={index}>
                                    <img
                                        style={{
                                            width: '100%',
                                            height: '400',
                                            maxHeight: '330px',
                                            objectFit: 'cover'
                                        }}
                                        src={val.background}
                                        alt=""
                                    />
                                </Carousel.Item>
                            ))
                        }
                    </Carousel>
                </Container>
            )}
            <ScreenHeader>
                <Nav
                    variant={'tabs'}
                    defaultActiveKey={'/shop/'}
                >
                    {showServices && (
                        <Nav.Item>
                            <Nav.Link
                                as={Link}
                                to={ROUTER_URL.SHOPS_ID_SERVICES({ shopId })}
                                active={servicesMatch}
                                data-testid='data-test-shop-services'
                            >
                                {servicesTabLabel}
                            </Nav.Link>
                        </Nav.Item>
                    )}

                    {showGroups && (
                        <Nav.Item>
                            <Nav.Link
                                as={Link}
                                to={ROUTER_URL.SHOPS_ID_GROUPS({ shopId })}
                                active={groupsMatch}
                                data-testid='data-test-shop-groups'
                            >
                                {groupsTabLabel}
                            </Nav.Link>
                        </Nav.Item>
                    )}

                    {showEvents && (
                        <Nav.Item>
                            <Nav.Link
                                as={Link}
                                to={ROUTER_URL.SHOPS_ID_EVENTS({ shopId })}
                                active={eventsMatch}
                                data-testid='data-test-shop-events'
                            >
                                {eventsTabLabel}
                            </Nav.Link>
                        </Nav.Item>
                    )}

                    {showSubscriptions && (
                        <Nav.Item>
                            <Nav.Link
                                as={Link}
                                to={ROUTER_URL.SHOPS_ID_SUBSCRIPTIONS({ shopId })}
                                active={subscriptionsMatch}
                                data-testid='data-test-shop-subscriptions'
                            >
                                {subscriptionsTabLabel}
                            </Nav.Link>
                        </Nav.Item>
                    )}

                    {showBundles && (
                        <Nav.Item>
                            <Nav.Link
                                as={Link}
                                to={ROUTER_URL.SHOPS_ID_BUNDLES({ shopId })}
                                active={bundlesMatch}
                                data-testid='data-test-shop-bundles'
                            >
                                {bundlesTabLabel}
                            </Nav.Link>
                        </Nav.Item>
                    )}
                </Nav>
            </ScreenHeader>
            <Switch>
                <Route
                    path={ROUTER_PATH.SHOPS_ID}
                    exact
                    component={() => (
                        <Redirect to={ROUTER_URL.SHOPS_ID_SERVICES({ shopId })} />
                    )}
                />
                <Route
                    path={ROUTER_PATH.SHOPS_ID_SERVICES}
                    component={Services}
                />
                <Route
                    path={ROUTER_PATH.SHOPS_ID_GROUPS}
                    component={Groups}
                />
                <Route
                    path={ROUTER_PATH.SHOPS_ID_EVENTS}
                    component={Events}
                />
                <Route
                    path={[
                        ROUTER_PATH.SHOPS_ID_SUBSCRIPTIONS_ID,
                        ROUTER_PATH.SHOPS_ID_SUBSCRIPTIONS
                    ]}
                    component={Subscriptions}
                />
                <Route
                    path={ROUTER_PATH.SHOPS_ID_BUNDLES}
                    component={Bundles}
                />
                <Route
                    exact
                    path="*"
                    component={() => (
                        <Redirect to={ROUTER_URL.SHOPS_ID({ shopId })}/>
                    )}
                />
            </Switch>
        </>
    );
};

export default Shop
